// The app shell for Staff and Ops (DESIGN.md: layouts). Adapted from shadcn/ui blocks
// sidebar-07 (collapses to icons) and sidebar-16 (sticky header):
//
//   ┌ sidebar ─────────┬ header: [≡] Club › Page ─────────── actions ┐
//   │ context switcher │                                             │
//   │ nav groups       │ page content                                │
//   │ …                │                                             │
//   │ user menu        │                                             │
//   └──────────────────┴─────────────────────────────────────────────┘
//
// Router-agnostic: nav items render through `linkAs` (pass your router's link, adapted to
// take `href`). Collapse with the header button or Cmd/Ctrl+B; on phones the sidebar is a sheet.
//
// Member web passes `mobileTabs`: on phones, a bottom tab bar with the primary destinations
// (one tap, in thumb reach, like the iPhone app) plus "More", which opens the sidebar sheet for
// everything else. Tablets and desktops keep the sidebar only.

import { Avatar, AvatarFallback } from "@union/ui/components/avatar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@union/ui/components/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@union/ui/components/dropdown-menu";
import { Separator } from "@union/ui/components/separator";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@union/ui/components/sidebar";
import { cn } from "@union/ui/lib/utils";
import { ChevronsUpDown, LogOut, type LucideIcon, Menu } from "lucide-react";
import type * as React from "react";
import { Fragment, type ReactNode } from "react";

export interface AppNavItem {
  title: string;
  href: string;
  icon: LucideIcon;
  active?: boolean;
  /** A count or short status beside the item ("3"). */
  badge?: string;
  /** In the mobile tab bar: one short word when the title is longer ("Bookings"). */
  tabTitle?: string;
}

export interface AppNavGroup {
  /** Omit for the first, main group. */
  label?: string;
  items: AppNavItem[];
}

export interface AppContext {
  /** What's being switched between, for screen readers and the menu's heading ("Clubs"). */
  label: string;
  current: { id: string; name: string; detail?: string };
  options: { id: string; name: string; detail?: string }[];
  onSelect: (id: string) => void;
}

export interface AppUser {
  name: string;
  detail?: string;
  onSignOut: () => void;
}

export interface Crumb {
  label: string;
  href?: string;
}

type LinkComponent = React.ComponentType<React.ComponentProps<"a"> & { href: string }>;

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

export function AppShell({
  product,
  context,
  nav,
  user,
  breadcrumbs = [],
  actions,
  linkAs = "a" as unknown as LinkComponent,
  defaultOpen = true,
  mobileTabs,
  children,
}: {
  /** "Union Staff", "Union Ops": shown when there's no context switcher. */
  product: string;
  context?: AppContext;
  nav: AppNavGroup[];
  user: AppUser;
  breadcrumbs?: Crumb[];
  /** Page controls on the right of the header (a date, search, one primary action). */
  actions?: ReactNode;
  linkAs?: LinkComponent;
  defaultOpen?: boolean;
  /** Phones only: up to four primary destinations as bottom tabs, plus "More" (the sidebar). */
  mobileTabs?: AppNavItem[];
  children: ReactNode;
}) {
  if (mobileTabs && mobileTabs.length > 4) throw new Error("AppShell: at most four mobile tabs (plus More)");
  const Link = linkAs;
  return (
    <SidebarProvider defaultOpen={defaultOpen}>
      <Sidebar collapsible="icon">
        <SidebarHeader>
          {context ? <ContextSwitcher context={context} /> : <ProductMark product={product} />}
        </SidebarHeader>
        <SidebarContent>
          {nav.map((group, i) => (
            <SidebarGroup key={group.label ?? i}>
              {group.label && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
              <SidebarMenu>
                {group.items.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton asChild isActive={!!item.active} tooltip={item.title}>
                      <Link href={item.href} aria-current={item.active ? "page" : undefined}>
                        <item.icon />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                    {item.badge && <SidebarMenuBadge>{item.badge}</SidebarMenuBadge>}
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroup>
          ))}
        </SidebarContent>
        <SidebarFooter>
          <UserMenu user={user} />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>
      <SidebarInset>
        <header className="sticky top-0 z-10 flex min-h-14 shrink-0 flex-wrap items-center gap-x-space-2 gap-y-space-2 border-b border-border bg-background px-space-4 py-space-2">
          {/* With tabs, "More" opens the menu on phones. */}
          <SidebarTrigger className={cn("-ml-space-1", mobileTabs && "max-md:hidden")} />
          {breadcrumbs.length > 0 && (
            <>
              <Separator orientation="vertical" className="mx-space-1 !h-5 max-sm:hidden" />
              <Breadcrumb className="min-w-0">
                <BreadcrumbList className="flex-nowrap">
                  {breadcrumbs.map((crumb, i) => (
                    <Fragment key={crumb.label}>
                      {i > 0 && <BreadcrumbSeparator className="max-sm:hidden" />}
                      <BreadcrumbItem
                        className={cn("truncate", i < breadcrumbs.length - 1 && "max-sm:hidden")}
                      >
                        {i === breadcrumbs.length - 1 || !crumb.href ? (
                          <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink asChild>
                            <Link href={crumb.href}>{crumb.label}</Link>
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                    </Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            </>
          )}
          {actions && (
            <div className="ml-auto flex flex-wrap items-center justify-end gap-space-2">{actions}</div>
          )}
        </header>
        <div
          className={cn(
            "flex flex-1 flex-col gap-space-5 p-space-5 max-sm:p-space-4",
            // Keep the last content clear of the tab bar (and the home indicator).
            mobileTabs &&
              "max-md:pb-[calc(var(--spacing-row-comfy)+env(safe-area-inset-bottom)+var(--spacing-space-4))]",
          )}
        >
          {children}
        </div>
        {mobileTabs && <MobileTabs tabs={mobileTabs} linkAs={Link} />}
      </SidebarInset>
    </SidebarProvider>
  );
}

/** Bottom tabs on phones (member web): primary destinations, then More for the sidebar. */
function MobileTabs({ tabs, linkAs: Link }: { tabs: AppNavItem[]; linkAs: LinkComponent }) {
  const { openMobile, setOpenMobile } = useSidebar();
  const tab =
    "relative flex min-w-0 flex-1 flex-col items-center justify-center gap-0.5 type-caption text-muted-foreground transition-colors focus-visible:outline-offset-[-2px] [&>svg]:size-5 [&>svg]:[stroke-width:1.75]";
  // The current tab: primary-text, with a bar along its top edge.
  const current =
    "text-primary-text before:absolute before:inset-x-[25%] before:top-0 before:h-0.5 before:rounded-pill before:bg-primary-text";
  return (
    <nav
      aria-label="Main"
      data-slot="mobile-tabs"
      className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <div className="flex h-row-comfy items-stretch">
        {tabs.map((item) => (
          <Link
            key={item.href}
            href={item.href}
            aria-current={item.active ? "page" : undefined}
            className={cn(tab, item.active && current)}
          >
            <item.icon aria-hidden="true" />
            <span className="max-w-full truncate px-space-1">{item.tabTitle ?? item.title}</span>
            {item.badge && (
              <span className="absolute top-space-1 left-[calc(50%+0.5rem)] min-w-4 rounded-pill bg-primary px-1 text-center type-caption text-primary-foreground">
                {item.badge}
              </span>
            )}
          </Link>
        ))}
        <button
          type="button"
          className={cn(tab, openMobile && current)}
          aria-expanded={openMobile}
          onClick={() => setOpenMobile(!openMobile)}
        >
          <Menu aria-hidden="true" />
          <span>More</span>
        </button>
      </div>
    </nav>
  );
}

function ProductMark({ product }: { product: string }) {
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <div className="flex h-12 items-center gap-space-2 px-space-2 group-data-[collapsible=icon]:px-0">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-sm bg-primary type-label text-primary-foreground">
            U
          </span>
          <span className="truncate type-label group-data-[collapsible=icon]:hidden">{product}</span>
        </div>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/** Which club (or other context) you're working in. Adapted from the team switcher recipe. */
function ContextSwitcher({ context }: { context: AppContext }) {
  const { isMobile } = useSidebar();
  const { current, options } = context;
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={options.length < 2}>
            <SidebarMenuButton
              size="lg"
              aria-label={`${context.label}: ${current.name}`}
              className="data-[state=open]:bg-sidebar-accent"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-sm bg-primary type-label text-primary-foreground">
                {initials(current.name)}
              </span>
              <span className="grid flex-1 text-left">
                <span className="truncate type-label" data-testid="context-name">
                  {current.name}
                </span>
                {current.detail && (
                  <span className="truncate type-caption text-muted-foreground">{current.detail}</span>
                )}
              </span>
              {options.length > 1 && <ChevronsUpDown className="ml-auto" />}
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
            align="start"
            side={isMobile ? "bottom" : "right"}
          >
            <DropdownMenuLabel>{context.label}</DropdownMenuLabel>
            {options.map((option) => (
              <DropdownMenuItem key={option.id} onSelect={() => context.onSelect(option.id)}>
                <span className="flex size-6 shrink-0 items-center justify-center rounded-sm border border-border type-caption">
                  {initials(option.name)}
                </span>
                <span className="grid">
                  <span>{option.name}</span>
                  {option.detail && (
                    <span className="type-caption text-muted-foreground">{option.detail}</span>
                  )}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

/** Who's signed in, and signing out. Adapted from the nav-user recipe. */
function UserMenu({ user }: { user: AppUser }) {
  const { isMobile } = useSidebar();
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              aria-label={`Account: ${user.name}`}
              className="data-[state=open]:bg-sidebar-accent"
            >
              <Avatar>
                <AvatarFallback>{initials(user.name)}</AvatarFallback>
              </Avatar>
              <span className="grid flex-1 text-left">
                <span className="truncate type-label">{user.name}</span>
                {user.detail && (
                  <span className="truncate type-caption text-muted-foreground">{user.detail}</span>
                )}
              </span>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56"
            side={isMobile ? "bottom" : "right"}
            align="end"
          >
            <DropdownMenuLabel>{user.name}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={user.onSignOut}>
              <LogOut /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
